import { useEffect, useRef, type MutableRefObject } from 'react';
import { useRecordingGuardContext } from '../contexts/RecordingGuardContext';

/** history.state key that marks the entry this hook pushed; its value is the trap's id. */
export const TRAP_KEY = '__recordingGuardTrap';
/** Upper bound on waiting for the browser to confirm that history.back() has happened. */
const POP_TIMEOUT_MS = 500;

interface Trap {
  id: string;
  /** Path of the Scribe page the trap sits on; used to ignore jumps to another page. */
  pathname: string;
  /** Set while history.back() is removing the trap entry. */
  popping: Promise<void> | null;
  /** Pending deferred removal (cancelled when StrictMode re-runs the effect). */
  removeTimer: ReturnType<typeof setTimeout> | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isTrapEntry = (id: string): boolean => {
  const state: unknown = window.history.state;
  return isRecord(state) && state[TRAP_KEY] === id;
};

/**
 * Pushes an entry at the same URL. It copies the current state (React Router's key, idx and
 * usr) so the router reads it as the page it is already on, and adds the marker.
 */
const pushTrapEntry = (id: string): void => {
  const state: unknown = window.history.state;
  window.history.pushState({ ...(isRecord(state) ? state : {}), [TRAP_KEY]: id }, '', window.location.href);
};

/**
 * Steps back past the trap entry, but only while it is still the current entry (if the user
 * has since moved on, going back would land on the wrong page). Resolves once the browser has
 * made the move. Safe to call repeatedly: later calls share the first one's promise.
 */
function removeTrapEntry(ref: MutableRefObject<Trap | null>): Promise<void> {
  const trap = ref.current;
  if (!trap) return Promise.resolve();
  if (trap.popping) return trap.popping;
  if (trap.removeTimer) {
    clearTimeout(trap.removeTimer);
    trap.removeTimer = null;
  }
  if (!isTrapEntry(trap.id)) {
    ref.current = null;
    return Promise.resolve();
  }
  trap.popping = new Promise<void>((resolve) => {
    const finish = () => {
      window.removeEventListener('popstate', finish);
      clearTimeout(fallback);
      if (ref.current === trap) ref.current = null;
      resolve();
    };
    const fallback = setTimeout(finish, POP_TIMEOUT_MS);
    window.addEventListener('popstate', finish);
    window.history.back();
  });
  return trap.popping;
}

/**
 * While `active` is true (a recording is live):
 *  - asks the browser to show its native "Leave site?" prompt on refresh or tab close;
 *  - turns the browser Back and Forward buttons into the in-page "Recording in progress"
 *    dialog. BrowserRouter has no useBlocker, so one trap entry (same URL) is pushed on top of
 *    the page. A history move off it re-pushes the trap and asks `confirmLeave`: Continue
 *    Recording stays, Leave Page releases the microphone and then steps back past the trap
 *    and the page.
 * The trap entry is removed when the recording stops. Nothing is registered while inactive.
 */
export function useRecordingGuard(active: boolean): void {
  const { confirmLeave, registerLeaveCleanup, isLeavePending } = useRecordingGuardContext();
  const trapRef = useRef<Trap | null>(null);
  const dialogOpenRef = useRef(false);

  useEffect(() => {
    if (!active) return undefined;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Required by older Chromium builds to trigger the prompt.
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [active]);

  useEffect(() => {
    if (!active) return undefined;

    // StrictMode runs setup, cleanup, setup in one go: the cleanup only schedules the
    // removal, so the second setup finds the same trap and cancels it instead of pushing again.
    let trap = trapRef.current;
    if (trap && !trap.popping) {
      if (trap.removeTimer) clearTimeout(trap.removeTimer);
      trap.removeTimer = null;
    } else {
      trap = {
        id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`,
        pathname: window.location.pathname,
        popping: null,
        removeTimer: null,
      };
      trapRef.current = trap;
      pushTrapEntry(trap.id);
    }
    const activeTrap = trap;

    const handlePopState = () => {
      // Our own history.back() while removing the trap must not open a dialog.
      if (activeTrap.popping) return;
      // Landed on the trap itself: still on the page, nothing to do.
      if (isTrapEntry(activeTrap.id)) return;
      // A jump to another page (long-press history menu) cannot be undone from here.
      if (window.location.pathname !== activeTrap.pathname) return;

      // Back or Forward moved off the trap onto the page entry below it: put the trap back.
      pushTrapEntry(activeTrap.id);
      // A second press while the dialog is open keeps the trap but not a second dialog. This
      // includes a dialog opened by a Navbar link or Sign out: that caller owns the outcome, and
      // a second continuation here would step back over the page it navigated to.
      if (dialogOpenRef.current || isLeavePending()) return;
      dialogOpenRef.current = true;
      confirmLeave().then(
        async (leave) => {
          dialogOpenRef.current = false;
          if (!leave) return;
          // The microphone is already released. Drop the trap, then leave the page.
          await removeTrapEntry(trapRef);
          window.history.back();
        },
        () => {
          dialogOpenRef.current = false;
        },
      );
    };

    window.addEventListener('popstate', handlePopState);
    // Navbar links and Sign out confirm through the context; they wait for the trap to go
    // before navigating so their new entry is not pushed on top of it.
    registerLeaveCleanup(() => removeTrapEntry(trapRef));

    return () => {
      window.removeEventListener('popstate', handlePopState);
      registerLeaveCleanup(null);
      if (activeTrap.popping) return;
      activeTrap.removeTimer = setTimeout(() => {
        activeTrap.removeTimer = null;
        void removeTrapEntry(trapRef);
      }, 0);
    };
  }, [active, confirmLeave, registerLeaveCleanup, isLeavePending]);
}
